import { memo } from "react";
import clsx from "clsx";
import type { POI } from "@poi-planner/shared";
import { formatTimeBudget, formatDistance } from "../utils/format";
import { hasUsableCoordinates } from "../utils/dayMapPois";
import { useLibraryStore } from "../store/libraryStore";
import { resolveTagNames } from "../utils/tagDisplay";
import { Button } from "./Button";

interface SidebarPoiCardProps {
  poi: POI;
  // Visual states
  isSelected?: boolean;
  isTargeted?: boolean;
  isDraggable?: boolean;
  // Badges
  scheduledCount?: number;
  distance?: number;
  showDistance?: boolean;
  // Pin state (trip context)
  isPinned?: boolean;
  isScheduled?: boolean;
  hasLegs?: boolean;
  onTogglePin?: (poiId: string) => Promise<string | null>;
  onShowPinMessage?: (message: string) => void;
  // Callbacks
  onClick?: () => void;
  onEdit?: () => void;
  /** Whether onEdit opens a saveable editor (true) or a read-only viewer (false). Defaults true. */
  canEdit?: boolean;
  onSetTarget?: (poiId: string | null) => void;
}

function SidebarPoiCardComponent({
  poi,
  isSelected = false,
  isTargeted = false,
  isDraggable = false,
  scheduledCount = 0,
  distance,
  showDistance = false,
  isPinned = false,
  isScheduled = false,
  hasLegs = false,
  onTogglePin,
  onShowPinMessage,
  onClick,
  onEdit,
  canEdit = true,
  onSetTarget,
}: SidebarPoiCardProps) {
  const managedTags = useLibraryStore((state) => state.tags);
  const tagNames = resolveTagNames(poi.tags, managedTags);
  const hasCoords = hasUsableCoordinates(poi);

  return (
    <div
      data-poi-id={poi.id}
      onClick={onClick}
      className={clsx(
        "group p-3 rounded-xl border cursor-pointer transition-all",
        "hover:shadow-md",
        isSelected
          ? "border-blue-500 bg-blue-50 shadow-sm"
          : "border-border bg-white hover:border-slate-300",
      )}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <span
            className={clsx(
              "text-sm font-bold block truncate",
              isSelected ? "text-blue-700" : "text-primary",
            )}
          >
            {poi.name}
          </span>
          {poi.ownerName && (
            <span className="text-2xs font-medium text-tertiary block truncate">
              {poi.ownerName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Distance badge */}
          {showDistance &&
            (distance !== undefined ? (
              <span className="text-xs font-bold px-1.5 py-px rounded bg-blue-500/10 text-blue-500">
                {formatDistance(distance)}
              </span>
            ) : (
              <span className="text-xs font-semibold px-1.5 py-px rounded bg-subtle text-gray-400 italic">
                No location
              </span>
            ))}
          {/* No location badge (when not showing distance) */}
          {!showDistance && !hasCoords && (
            <span
              className="text-xs font-semibold px-1.5 py-px rounded bg-amber-100 text-amber-700"
              title="No coordinates"
            >
              No location
            </span>
          )}
          {/* Scheduled count badge */}
          {scheduledCount > 0 && (
            <span className="text-xs font-bold px-1.5 py-px rounded bg-blue-100 text-blue-800">
              {scheduledCount}x
            </span>
          )}
          {/* Pin button (trip context only) */}
          {onTogglePin && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                void onTogglePin(poi.id).then((message) => {
                  if (message) onShowPinMessage?.(message);
                });
              }}
              title={
                isPinned && isScheduled && !hasLegs
                  ? "Remove from day first before unpinning"
                  : isPinned && hasLegs
                    ? "Manage pin"
                    : isPinned
                      ? "Remove pin"
                      : "Pin to trip"
              }
              aria-label={
                isPinned && hasLegs
                  ? `Manage pin for ${poi.name}`
                  : isPinned
                    ? `Remove pin from ${poi.name}`
                    : `Pin ${poi.name} to trip`
              }
              variant="icon"
              size="sm"
              icon={isPinned ? "keep" : "keep_off"}
              selected={isPinned}
              className={clsx(
                isPinned
                  ? "text-purple-600 opacity-100"
                  : "text-secondary reveal-on-card-hover hover:bg-purple-500/10 hover:text-purple-500",
              )}
            />
          )}
          {/* Target button */}
          {hasCoords && onSetTarget && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSetTarget(isTargeted ? null : poi.id);
              }}
              title={
                isTargeted
                  ? "Clear proximity target"
                  : "Sort by distance from this POI"
              }
              className={clsx(
                "flex items-center justify-center size-6 rounded-md transition-all",
                isTargeted
                  ? "bg-blue-500 text-white"
                  : "text-secondary reveal-on-card-hover hover:bg-blue-500/10 hover:text-blue-500",
              )}
            >
              <span className="material-symbols-outlined text-base">
                gps_fixed
              </span>
            </button>
          )}
          {/* Edit/view button */}
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className={clsx(
                "flex items-center justify-center size-6 rounded-md transition-all",
                isSelected
                  ? "hover:bg-blue-200 text-blue-600"
                  : "text-secondary reveal-on-card-hover hover:bg-slate-100",
              )}
              title={canEdit ? "Edit POI" : "View POI"}
            >
              <span className="material-symbols-outlined text-base">
                {canEdit ? "edit" : "visibility"}
              </span>
            </button>
          )}
          {/* Drag indicator */}
          {isDraggable && (
            <span className="material-symbols-outlined text-gray-300 text-lg">
              drag_indicator
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-secondary text-xs mt-1">
        <span className="material-symbols-outlined text-sm">schedule</span>
        <span>{formatTimeBudget(poi.timeBudget)}</span>
      </div>

      {tagNames.length > 0 && (
        <div className="flex gap-1 flex-wrap mt-2">
          {tagNames.map((tag) => (
            <span
              key={tag}
              className={clsx(
                "text-xs py-0.5 px-1.5 rounded-md font-medium",
                isSelected
                  ? "bg-blue-200 text-blue-700"
                  : "bg-slate-100 text-secondary",
              )}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export const SidebarPoiCard = memo(SidebarPoiCardComponent);
SidebarPoiCard.displayName = "SidebarPoiCard";
